import { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const keyOf = (propertyId, buildingName) => `${propertyId}::${buildingName}`;

/**
 * The "Multiple buildings…" checklist of the invoice form. One vendor invoice
 * often covers many buildings; ticking them here adds them to the form, one
 * section per property, each row priced on its own building. Buildings already on
 * the form show ticked and locked.
 *
 * `choices` comes from buildingChoicesByProperty: [{ propertyId, label, buildings: [name] }].
 */
export const BuildingPickerModal = ({ isOpen, choices = [], existing = [], onConfirm, onCancel }) => {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setPicked(new Set());
    }
  }, [isOpen]);

  const taken = useMemo(
    () => new Set(existing.map((e) => keyOf(e.propertyId, e.buildingName))),
    [existing]
  );

  // A property matching the search shows all its buildings; otherwise only the
  // buildings whose own name matches.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return choices;
    return choices
      .map((p) => (String(p.label).toLowerCase().includes(q)
        ? p
        : { ...p, buildings: p.buildings.filter((b) => b.toLowerCase().includes(q)) }))
      .filter((p) => p.buildings.length);
  }, [choices, search]);

  const toggle = (k) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const toggleProperty = (p) => {
    const open = p.buildings.map((b) => keyOf(p.propertyId, b)).filter((k) => !taken.has(k));
    const allOn = open.length > 0 && open.every((k) => picked.has(k));
    setPicked((prev) => {
      const next = new Set(prev);
      open.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  // Checklist order, so the new sections appear the way they were listed.
  const confirm = () =>
    onConfirm(
      choices.flatMap((p) =>
        p.buildings
          .filter((b) => picked.has(keyOf(p.propertyId, b)))
          .map((b) => ({ propertyId: p.propertyId, buildingName: b }))
      )
    );

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered scrollable>
      <ModalHeader toggle={onCancel}>Multiple buildings</ModalHeader>
      <ModalBody>
        <div style={{ color: "#64748B", fontSize: "0.8rem", marginBottom: 10 }}>
          Tick every building this invoice covers. Each gets its own section to enter that building's amount.
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search property or building"
          style={{ marginBottom: 12 }}
          autoFocus
        />
        {visible.length === 0 && (
          <div style={{ color: "#94A3B8", fontSize: "0.82rem", padding: "12px 0" }}>No buildings match.</div>
        )}
        {visible.map((p) => {
          const open = p.buildings.filter((b) => !taken.has(keyOf(p.propertyId, b)));
          const allOn = open.length > 0 && open.every((b) => picked.has(keyOf(p.propertyId, b)));
          return (
            <div key={p.propertyId} style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: "0.84rem", color: "#1E293B", marginBottom: 4 }}>
                <input type="checkbox" checked={allOn} disabled={!open.length} onChange={() => toggleProperty(p)} />
                {p.label}
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingLeft: 24 }}>
                {p.buildings.map((b) => {
                  const k = keyOf(p.propertyId, b);
                  const isTaken = taken.has(k);
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: isTaken ? "#94A3B8" : "#334155" }}>
                      <input type="checkbox" checked={isTaken || picked.has(k)} disabled={isTaken} onChange={() => toggle(k)} />
                      {b}
                      {isTaken && <span style={{ fontSize: "0.72rem" }}>(already on this invoice)</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-light" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!picked.size} onClick={confirm}>
          <FeatherIcon icon="plus" size={14} /> Add {picked.size || ""} building{picked.size === 1 ? "" : "s"}
        </button>
      </ModalFooter>
    </Modal>
  );
};
